// ADR 28.9.26d §1 — the entry point is a module function, not a hook.
//
// 28.9.26f raises a snackbar from places that are not React (`speechPlayer.ts`'s
// `onerror`, `speech/missingVoice.ts`), as Android's `SpeakWarning` is a
// Compose-free `fun interface` (`SpeakOrWarn.kt:104-111`). A hook or a context
// would force every such caller to grow a registration seam, so the queue lives
// at module level and `SnackbarHost` only subscribes — the shape `UpdateToast`
// already has over `pwa/register.ts`. This file imports neither React nor
// `window`, so a node-project module (and its test) can call it.
//
// A `showSnackbar` with no host mounted waits in the queue and shows when one
// subscribes, as a `SnackbarHostState` holds its queue before its host composes.

import {
  EMPTY_QUEUE,
  defaultDuration,
  snackbarReducer,
  type SnackbarEvent,
  type SnackbarQueueState,
  type SnackbarRequest,
} from './snackbarQueue'

let state: SnackbarQueueState = EMPTY_QUEUE
let nextId = 1
const listeners = new Set<() => void>()

function dispatch(event: SnackbarEvent): void {
  const next = snackbarReducer(state, event)
  if (next === state) return
  state = next
  for (const listener of listeners) listener()
}

/** Queue a snackbar; returns its id, for `dismissSnackbar`. */
export function showSnackbar(request: SnackbarRequest): number {
  const id = nextId++
  dispatch({
    type: 'show',
    entry: { id, message: request.message, action: request.action, duration: defaultDuration(request) },
  })
  return id
}

/** Take a snackbar down, whether it is showing or still waiting. */
export function dismissSnackbar(id: number): void {
  dispatch({ type: 'dismiss', id })
}

/** The host's timer: a no-op unless `id` is still the one showing. */
export function expireSnackbar(id: number): void {
  dispatch({ type: 'expire', id })
}

export function subscribeSnackbars(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The same reference between changes — `useSyncExternalStore`'s contract. */
export function snackbarState(): SnackbarQueueState {
  return state
}

/**
 * Tests only: empty the queue between tests (`testSetup.ts`'s `afterEach`),
 * so one test's leftover entry cannot show in the next. Ids keep counting, so
 * an id is never reused within a run.
 */
export function resetSnackbars(): void {
  if (state === EMPTY_QUEUE) return
  state = EMPTY_QUEUE
  for (const listener of listeners) listener()
}
